import styled from 'styled-components'

/** Thumbnail strip / pill; aligns with SwiftXR “AR” chrome (light gray). */
export const SHOWCASE_AVATAR_BG = '#ebebeb'

/** Main row under the top tabs (fills remaining height in the 1000×600 frame). */
export const ShowcaseMainRow = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: row;
	align-items: stretch;
	padding: 18px 28px 22px 32px;
	gap: 24px;
	box-sizing: border-box;
	overflow: hidden;
`

export const VisualColumn = styled.div`
	flex: 0 0 70%;
	min-width: 0;
	display: flex;
	align-items: stretch;
`

export const VisualCard = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	border-radius: 20px;
	overflow: hidden;
	background: #0b1f19;
	box-shadow: 0 24px 48px rgba(8, 55, 46, 0.2),
		0 0 0 1px rgba(255, 255, 255, 0.06) inset;
`

/** See it in locations tab (`/showcase/environments`): full-bleed media with overlaid thumbnails. */
export const VisualEnvironmentCard = styled.div`
	position: relative;
	flex: 1;
	min-height: 0;
	border-radius: 20px;
	overflow: hidden;
	background: #0b1f19;
	box-shadow: 0 24px 48px rgba(8, 55, 46, 0.2),
		0 0 0 1px rgba(255, 255, 255, 0.06) inset;
`

/** Media (iframe / carousel) only; thumbnails use ThumbnailDockRail below. */
export const VisualMediaArea = styled.div`
	flex: 1;
	min-height: 0;
	position: relative;
	overflow: hidden;
`

/** Fixed-height footer strip for the thumbnail pill; keeps controls off the product. */
export const ThumbnailDockRail = styled.div`
	flex-shrink: 0;
	display: flex;
	justify-content: center;
	align-items: flex-end;
	padding: 4px 10px 8px;
	background: ${SHOWCASE_AVATAR_BG};
	border-top: 1px solid rgba(0, 0, 0, 0.06);
`

export const IframeLayer = styled.div`
	position: absolute;
	inset: 0;
	background: #061512;
`

export const PhotoLayer = styled.div`
	position: absolute;
	inset: 0;
	background: #0a1210;
	display: flex;
	align-items: center;
	justify-content: center;
`

/** Fills the visual card; `cover` avoids side letterboxing on wide containers (crops edges if aspect ratios differ). */
export const PhotoImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center center;
	display: block;
`

export const ThumbnailDock = styled.div<{ $overlay?: boolean }>`
	${(p) =>
		p.$overlay
			? `
		position: absolute;
		left: 50%;
		bottom: 14px;
		transform: translateX(-50%);
		z-index: 2;
		background: rgba(6, 21, 18, 0.48);
		backdrop-filter: blur(18px) saturate(180%);
		-webkit-backdrop-filter: blur(18px) saturate(180%);
		border: 1px solid rgba(255, 255, 255, 0.16);
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35),
			inset 0 1px 0 rgba(255, 255, 255, 0.1);
	`
			: `
		background: ${SHOWCASE_AVATAR_BG};
		border: 1px solid rgba(0, 0, 0, 0.06);
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
	`}
	display: inline-flex;
	max-width: calc(100% - 24px);
	flex-direction: row;
	align-items: flex-end;
	justify-content: center;
	flex-wrap: nowrap;
	gap: 5px;
	padding: 6px 10px;
	border-radius: 16px;
	box-sizing: border-box;
	overflow-x: auto;
	overflow-y: hidden;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
`

export const ThumbButton = styled.button<{ $active: boolean; $glass?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	padding: 6px 3px 5px;
	flex: 0 0 auto;
	min-width: 0;
	border: none;
	border-radius: 12px;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;
	background: ${(p) =>
		p.$glass
			? p.$active
				? 'rgba(255, 255, 255, 0.22)'
				: 'rgba(255, 255, 255, 0.08)'
			: p.$active
				? 'rgba(19, 144, 81, 0.14)'
				: 'rgba(255, 255, 255, 0.85)'};
	box-shadow: ${(p) =>
		p.$glass
			? p.$active
				? '0 0 0 3px #139051, 0 8px 22px rgba(19, 144, 81, 0.35)'
				: '0 0 0 1px rgba(255, 255, 255, 0.14)'
			: p.$active
				? '0 0 0 3px #139051, 0 4px 14px rgba(19, 144, 81, 0.2)'
				: '0 0 0 1px rgba(0, 0, 0, 0.1)'};
	transform: ${(p) => (p.$active ? 'scale(1.03)' : 'scale(1)')};
	transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

	&:hover {
		background: ${(p) =>
			p.$glass
				? p.$active
					? 'rgba(255, 255, 255, 0.26)'
					: 'rgba(255, 255, 255, 0.14)'
				: p.$active
					? 'rgba(19, 144, 81, 0.18)'
					: 'rgba(255, 255, 255, 1)'};
	}
`

export const ThumbImage = styled.img<{ $compact?: boolean; $glass?: boolean }>`
	width: ${(p) => (p.$compact ? '68px' : '86px')};
	height: ${(p) => (p.$compact ? '48px' : '60px')};
	object-fit: cover;
	border-radius: 8px;
	display: block;
	background: ${(p) =>
		p.$glass ? 'rgba(0, 0, 0, 0.28)' : 'rgba(0, 0, 0, 0.06)'};
`

export const ThumbLabel = styled.span<{ $compact?: boolean; $glass?: boolean }>`
	font-family: 'GothamMedium', 'GothamBook', sans-serif;
	font-size: ${(p) => (p.$compact ? '9px' : '10px')};
	line-height: 1.15;
	color: ${(p) => (p.$glass ? 'rgba(255, 255, 255, 0.92)' : '#2a3f38')};
	text-align: center;
	white-space: nowrap;
	${(p) =>
		p.$compact
			? `
		max-width: 72px;
		overflow: hidden;
		text-overflow: ellipsis;
	`
			: ''}
`

export const InfoColumn = styled.aside`
	flex: 0 0 30%;
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 8px 4px 8px 8px;
	gap: 16px;
`

export const InfoTitle = styled.h1`
	margin: 0;
	font-family: 'GothamBold', sans-serif;
	font-size: 32px;
	line-height: 1.15;
	letter-spacing: -0.02em;
	color: #08372e;
`

/** Single line under title; site-aligned promise. */
export const InfoTagline = styled.p`
	margin: 0;
	font-family: 'GothamMedium', sans-serif;
	font-size: 13px;
	line-height: 1.4;
	color: #2a4a40;
	max-width: 32ch;
`

export const InfoBody = styled.p`
	margin: 0;
	font-family: 'GothamBook', sans-serif;
	font-size: 15px;
	line-height: 1.55;
	color: #2a3f38;
	max-width: 28ch;
`

export const QRBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	align-self: center;
	width: 100%;
	gap: 10px;
	margin-top: 2px;
`

export const QRImage = styled.img`
	width: 140px;
	height: 140px;
	border-radius: 12px;
	background: #fff;
	padding: 8px;
	box-shadow: 0 12px 28px rgba(8, 55, 46, 0.12);
`

/** Cross-fade wrapper; use $center for photos, default for full-bleed iframe. */
export const FadeLayer = styled.div<{ $center?: boolean }>`
	position: absolute;
	inset: 0;
	animation: showcaseMediaFadeIn 0.35s ease-out;
	display: ${(p) => (p.$center ? 'flex' : 'block')};
	align-items: center;
	justify-content: center;

	@keyframes showcaseMediaFadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
`

export const ShowcaseIframe = styled.iframe`
	border: none;
	display: block;
	width: 100%;
	height: 100%;
`
